import localforage from 'localforage';
import type {
  AudioRecCalibrationProfile,
  AudioRecExpectedEvent,
  RecognitionDetectedNote,
  RecognitionFrame,
  RecognitionPeak,
  RecognitionSuppressedNote,
} from '../types';
import type {
  LocalAudioBridgeEventDetail,
  LocalAudioGradeEventDetail,
} from 'Utils/LocalAudioSessionEvents';
import { serializeWaveformSnapshot } from './waveformCapture';

export const LOCAL_AUDIO_SESSION_MAX_FRAMES = 12_000;
export const LOCAL_AUDIO_SESSION_ENDPOINT = '/__museflow/audio-recognition-sessions';

export type LocalAudioExpectedSnapshot = {
  midiNotes: number[];
  expectationKey?: string;
  events?: AudioRecExpectedEvent[];
};

export type LocalAudioSessionFrame = {
  capturedAtMs: number;
  audioTimeSec: number;
  exercisePlaying: boolean;
  expectationKey: string;
  expectedMidiNotes: number[];
  expectedEvents?: AudioRecExpectedEvent[];
  recognizedMidiNotes: number[];
  onsetMidiNotes: number[];
  rms: number;
  minRms: number;
  noiseFloor: number;
  residualEnergy: number | null;
  recognitionMode: string;
  detectedNotes: RecognitionDetectedNote[];
  selectedNotes: RecognitionDetectedNote[];
  suppressedNotes: RecognitionSuppressedNote[];
  peaks: RecognitionPeak[];
  waveform?: number[];
};

export type LocalAudioSessionSummary = {
  expectedEventCount: number;
  recognizedEventCount: number;
  missedAfterSignalCount: number;
  noSignalCount: number;
  recognizedFrameCount: number;
  onsetCount: number;
  bridgeNoteOnCount: number;
  bridgeNoteOffCount: number;
  waveformSnapshotCount: number;
  gradeCounts: Record<string, number>;
  onsetGradeCounts: Record<string, number>;
  offsetGradeCounts: Record<string, number>;
  medianReleaseDecisionDelayMs: number | null;
  p95ReleaseDecisionDelayMs: number | null;
  activeBridgeNoteCountAtEnd: number;
};

export type LocalAudioSessionReport = {
  schemaVersion: 2;
  source: 'museflow-local-audio-session';
  sessionId: string;
  createdAtIso: string;
  startedAtIso: string;
  endedAtIso: string;
  durationMs: number;
  frameCount: number;
  truncated: boolean;
  metadata: Record<string, unknown>;
  calibrationProfile: AudioRecCalibrationProfile | null;
  summary: LocalAudioSessionSummary;
  bridgeEvents: LocalAudioBridgeEventDetail[];
  gradeEvents: LocalAudioGradeEventDetail[];
  frames: LocalAudioSessionFrame[];
};

type BuildLocalAudioSessionReportOptions = {
  sessionId: string;
  startedAtMs: number;
  endedAtMs: number;
  truncated: boolean;
  metadata: Record<string, unknown>;
  calibrationProfile: AudioRecCalibrationProfile | null;
  bridgeEvents: LocalAudioBridgeEventDetail[];
  gradeEvents: LocalAudioGradeEventDetail[];
};

const fallbackStore = localforage.createInstance({
  name: 'museflow-local-diagnostics',
  storeName: 'audio_recognition_sessions',
});

const normalizeMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((left, right) => left - right);

const notesEqual = (left: number[], right: number[]) =>
  left.length === right.length && left.every((note, index) => note === right[index]);

const cloneDetectedNotes = (notes: RecognitionDetectedNote[] | undefined) =>
  (notes ?? []).map((note) => ({ ...note }));

const cloneSuppressedNotes = (notes: RecognitionSuppressedNote[] | undefined) =>
  (notes ?? []).map((note) => ({ ...note }));

const clonePeaks = (peaks: RecognitionPeak[] | undefined) =>
  (peaks ?? [])
    .slice()
    .sort((left, right) => right.amp - left.amp)
    .slice(0, 16)
    .map((peak) => ({ ...peak }));

export const buildLocalAudioSessionFrame = (
  frame: RecognitionFrame,
  expected: LocalAudioExpectedSnapshot,
  exercisePlaying: boolean,
  capturedAtMs = Date.now(),
  captureWaveform = false,
): LocalAudioSessionFrame => {
  const waveform = captureWaveform ? serializeWaveformSnapshot(frame.waveform) : undefined;
  return {
    capturedAtMs,
    audioTimeSec: frame.timeSec,
    exercisePlaying,
    expectationKey: expected.expectationKey ?? '',
    expectedMidiNotes: normalizeMidiNotes(expected.midiNotes),
    expectedEvents: (expected.events ?? []).map((event) => ({ ...event })),
    recognizedMidiNotes: normalizeMidiNotes(frame.midiNotes ?? []),
    onsetMidiNotes: normalizeMidiNotes(frame.onsetMidiNotes ?? []),
    rms: frame.rms ?? 0,
    minRms: frame.minRms ?? 0,
    noiseFloor: frame.noiseFloor ?? 0,
    residualEnergy: frame.residualEnergy ?? null,
    recognitionMode: frame.recognitionMode ?? 'default',
    detectedNotes: cloneDetectedNotes(frame.detectedNotes),
    selectedNotes: cloneDetectedNotes(frame.selectedNotes),
    suppressedNotes: cloneSuppressedNotes(frame.suppressedNotes),
    peaks: clonePeaks(frame.peaks),
    ...(waveform ? { waveform } : {}),
  };
};

type ExpectedEvent = {
  expectedMidiNotes: number[];
  frames: LocalAudioSessionFrame[];
};

const collectExpectedEvents = (frames: LocalAudioSessionFrame[]) => {
  const events: ExpectedEvent[] = [];
  let current: ExpectedEvent | null = null;
  let currentKey = '';

  for (const frame of frames) {
    const key =
      frame.exercisePlaying && frame.expectedMidiNotes.length > 0
        ? frame.expectationKey || `midi:${frame.expectedMidiNotes.join(',')}`
        : '';

    if (!key) {
      current = null;
      currentKey = '';
      continue;
    }

    if (!current || key !== currentKey) {
      current = {
        expectedMidiNotes: frame.expectedMidiNotes,
        frames: [],
      };
      events.push(current);
      currentKey = key;
    }
    current.frames.push(frame);
  }

  return events;
};

export const summarizeLocalAudioSession = (
  frames: LocalAudioSessionFrame[],
  bridgeEvents: LocalAudioBridgeEventDetail[],
  gradeEvents: LocalAudioGradeEventDetail[],
): LocalAudioSessionSummary => {
  const expectedEvents = collectExpectedEvents(frames);
  let recognizedEventCount = 0;
  let missedAfterSignalCount = 0;
  let noSignalCount = 0;

  for (const expectedEvent of expectedEvents) {
    const hasSignal = expectedEvent.frames.some(
      (frame) => frame.minRms > 0 && frame.rms >= frame.minRms,
    );
    const recognized = expectedEvent.frames.some((frame) =>
      notesEqual(frame.expectedMidiNotes, frame.recognizedMidiNotes),
    );
    if (recognized) {
      recognizedEventCount += 1;
    } else if (hasSignal) {
      missedAfterSignalCount += 1;
    } else {
      noSignalCount += 1;
    }
  }

  const gradeCounts = gradeEvents.reduce<Record<string, number>>((counts, event) => {
    counts[event.state] = (counts[event.state] ?? 0) + 1;
    return counts;
  }, {});
  const gradeCountsForPhase = (phase: LocalAudioGradeEventDetail['phase']) =>
    gradeEvents
      .filter((event) => event.phase === phase)
      .reduce<Record<string, number>>((counts, event) => {
        counts[event.state] = (counts[event.state] ?? 0) + 1;
        return counts;
      }, {});
  const releaseDecisionDelaysMs = bridgeEvents
    .filter(
      (event) =>
        event.type === 'note-off' &&
        event.reason === 'release' &&
        Number.isFinite(event.observedAtMs),
    )
    .map((event) => Math.max(0, event.capturedAtMs - (event.observedAtMs as number)))
    .sort((left, right) => left - right);
  const percentile = (values: number[], value: number) => {
    if (values.length === 0) return null;
    const index = Math.max(0, Math.ceil(values.length * value) - 1);
    return values[Math.min(index, values.length - 1)];
  };
  const activeBridgeNotes = new Set<number>();
  bridgeEvents.forEach((event) => {
    if (event.type === 'note-on') activeBridgeNotes.add(event.midiNote);
    else activeBridgeNotes.delete(event.midiNote);
  });

  return {
    expectedEventCount: expectedEvents.length,
    recognizedEventCount,
    missedAfterSignalCount,
    noSignalCount,
    recognizedFrameCount: frames.filter((frame) => frame.recognizedMidiNotes.length > 0).length,
    onsetCount: frames.reduce((count, frame) => count + frame.onsetMidiNotes.length, 0),
    bridgeNoteOnCount: bridgeEvents.filter((event) => event.type === 'note-on').length,
    bridgeNoteOffCount: bridgeEvents.filter((event) => event.type === 'note-off').length,
    waveformSnapshotCount: frames.filter((frame) => frame.waveform != null).length,
    gradeCounts,
    onsetGradeCounts: gradeCountsForPhase('onset'),
    offsetGradeCounts: gradeCountsForPhase('offset'),
    medianReleaseDecisionDelayMs: percentile(releaseDecisionDelaysMs, 0.5),
    p95ReleaseDecisionDelayMs: percentile(releaseDecisionDelaysMs, 0.95),
    activeBridgeNoteCountAtEnd: activeBridgeNotes.size,
  };
};

export const buildLocalAudioSessionReport = (
  frames: LocalAudioSessionFrame[],
  options: BuildLocalAudioSessionReportOptions,
): LocalAudioSessionReport => ({
  schemaVersion: 2,
  source: 'museflow-local-audio-session',
  sessionId: options.sessionId,
  createdAtIso: new Date(options.endedAtMs).toISOString(),
  startedAtIso: new Date(options.startedAtMs).toISOString(),
  endedAtIso: new Date(options.endedAtMs).toISOString(),
  durationMs: Math.max(0, options.endedAtMs - options.startedAtMs),
  frameCount: frames.length,
  truncated: options.truncated,
  metadata: options.metadata,
  calibrationProfile: options.calibrationProfile,
  summary: summarizeLocalAudioSession(frames, options.bridgeEvents, options.gradeEvents),
  bridgeEvents: options.bridgeEvents,
  gradeEvents: options.gradeEvents,
  frames,
});

const saveFallback = async (report: LocalAudioSessionReport) => {
  await fallbackStore.setItem('latest', report);
  await fallbackStore.setItem(`session:${report.sessionId}`, report);
};

export const persistLocalAudioSession = async (
  report: LocalAudioSessionReport,
  keepalive = false,
) => {
  try {
    await saveFallback(report);
  } catch (error) {
    console.warn('[AudioRecognition] Could not save the local session in browser storage.', error);
  }

  const response = await fetch(LOCAL_AUDIO_SESSION_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
    keepalive,
  });
  if (!response.ok) {
    throw new Error(`Local audio session save failed with status ${response.status}.`);
  }
};

export const getLatestLocalAudioSession = () =>
  fallbackStore.getItem<LocalAudioSessionReport>('latest');
